import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiToggleLeft, FiToggleRight, FiMail } from 'react-icons/fi';
import { admin as adminApi } from '../../api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

export function CandidatesPage() {
  const [candidates, setCandidates] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCandidates({ search, page, limit: 50 });
      setCandidates(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (id, isActive) => {
    try {
      await adminApi.toggleCandidate(id);
      setCandidates(c => c.map(x => x.id === id ? { ...x, is_active: !isActive } : x));
      toast.success(isActive ? 'Candidate deactivated' : 'Candidate activated');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Candidates</h1>
        <p className="text-gray-500 text-sm">{pagination.total || 0} registered candidates</p>
      </div>
      <div className="card p-4 flex gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input className="input pl-9" placeholder="Search by name or email..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-gray-100">
            <th className="table-th">Candidate</th>
            <th className="table-th hidden md:table-cell">Phone</th>
            <th className="table-th hidden lg:table-cell">Exams Taken</th>
            <th className="table-th hidden lg:table-cell">Joined</th>
            <th className="table-th">Verified</th>
            <th className="table-th">Status</th>
            <th className="table-th text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(6)].map((_, i) => <tr key={i}>{[...Array(7)].map((_, j) => <td key={j} className="table-td"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>)
            : candidates.length === 0 ? <tr><td colSpan={7} className="py-12 text-center text-gray-400">No candidates found</td></tr>
            : candidates.map(c => (
              <tr key={c.id} className="hover:bg-gray-50/50">
                <td className="table-td">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-navy text-xs font-bold">{c.first_name?.[0]}{c.last_name?.[0]}</span>
                    </div>
                    <div><p className="font-semibold text-sm">{c.first_name} {c.last_name}</p><p className="text-xs text-gray-400">{c.email}</p></div>
                  </div>
                </td>
                <td className="table-td hidden md:table-cell text-sm text-gray-500">{c.phone || '—'}</td>
                <td className="table-td hidden lg:table-cell text-sm font-medium">{c.attempt_count || 0}</td>
                <td className="table-td hidden lg:table-cell text-xs text-gray-400">{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</td>
                <td className="table-td"><span className={c.is_verified ? 'badge-pass' : 'badge-fail'}>{c.is_verified ? 'Yes' : 'No'}</span></td>
                <td className="table-td"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{c.is_active ? 'Active' : 'Disabled'}</span></td>
                <td className="table-td text-right">
                  <button onClick={() => handleToggle(c.id, c.is_active)} className={`p-1.5 rounded text-lg ${c.is_active ? 'text-emerald-500 hover:text-red-500' : 'text-gray-300 hover:text-emerald-500'}`}>
                    {c.is_active ? <FiToggleRight /> : <FiToggleLeft />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50">
            <p className="text-sm text-gray-500">Page {page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p-1)} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">← Prev</button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p+1)} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidatesPage;
